import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../Common/Configurations/APIConfig';

const Profile = () => {
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [profileData, setProfileData] = useState({
        name: '',
        email: '',
        phoneNumber: '',
        city: '',
        address: '',
        role: '',
        isActive: true,
        userName: ''
    });
    const [formData, setFormData] = useState({ ...profileData });

    // Get user ID from localStorage
    const userId = localStorage.getItem('userId');

    useEffect(() => {
        if (userId) {
            fetchProfile();
        }
    }, [userId]);

    const fetchProfile = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${config.APIACTIVEURL}${config.GETUSERPROFILE}/${userId}`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                }
            );

            if (response.data.success) {
                setProfileData(response.data.data);
                setFormData(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
            alert('Failed to load profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            setLoading(true);

            // Prepare update data
            const updateData = {
                userId: userId,
                name: formData.name,
                email: formData.email,
                phoneNumber: formData.phoneNumber,
                city: formData.city,
                address: formData.address
            };

            const response = await axios.put(
                `${config.APIACTIVEURL}${config.UPDATEUSERPROFILE}`,
                updateData,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response.data.success) {
                setProfileData(response.data.data);
                setFormData(response.data.data);
                setIsEditing(false);
                alert('Profile updated successfully!');
            } else {
                alert(response.data.message || 'Failed to update profile.');
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            alert(error.response?.data?.message || 'An error occurred while updating profile.');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setFormData({ ...profileData });
        setIsEditing(false);
    };

    if (loading && !isEditing) {
        return (
            <div className="container-fluid py-4" style={{ paddingTop: '100px' }}>
                <div className="row">
                    <div className="col-12">
                        <div className="card shadow-sm border-0">
                            <div className="card-body text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">Loading profile...</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4" style={{ paddingTop: '100px' }}>
            <div className="row">
                <div className="col-12">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-0 py-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <h3 className="mb-0 fw-bold text-dark">Profile Settings</h3>
                            </div>
                        </div>

                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <div className="row">
                                    {/* Left Column - Profile Image & Personal Info */}
                                    <div className="col-lg-4">
                                        <div className="card border-0 shadow-sm mb-4">
                                            <div className="card-body text-center">
                                                <div className="position-relative d-inline-block mb-3">
                                                    <div className="rounded-circle overflow-hidden" 
                                                         style={{ 
                                                             width: '150px', 
                                                             height: '150px', 
                                                             backgroundColor: '#f8f9fa',
                                                             margin: '0 auto'
                                                         }}>
                                                        <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-primary bg-opacity-10">
                                                            <i className="ri-user-line text-primary" style={{ fontSize: '60px' }}></i>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <h4 className="mb-2">{isEditing ? (
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        value={formData.name}
                                                        onChange={handleInputChange}
                                                        className="form-control text-center fw-bold"
                                                        required
                                                        disabled={loading}
                                                    />
                                                ) : profileData.name}</h4>
                                                <p className="text-muted mb-3">{profileData.role}</p>
                                                
                                                <div className="d-grid gap-2">
                                                    {!isEditing ? (
                                                        <button 
                                                            type="button"
                                                            className="btn btn-primary"
                                                            onClick={() => setIsEditing(true)}
                                                            disabled={loading}
                                                        >
                                                            <i className="ri-edit-line me-1"></i> Edit Profile
                                                        </button>
                                                    ) : (
                                                        <div className="d-grid gap-2">
                                                            <button 
                                                                type="button"
                                                                className="btn btn-danger"
                                                                onClick={handleCancel}
                                                                disabled={loading}
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button 
                                                                type="submit"
                                                                className="btn btn-primary"
                                                                disabled={loading}
                                                            >
                                                                {loading ? (
                                                                    <>
                                                                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                                        Saving...
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <i className="ri-save-line me-1"></i> Save Changes
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Contact Information */}
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-header bg-white">
                                                <h5 className="mb-0 fw-bold">Contact Information</h5>
                                            </div>
                                            <div className="card-body">
                                                <div className="mb-3">
                                                    <label className="form-label text-muted small">Email</label>
                                                    {isEditing ? (
                                                        <input
                                                            type="email"
                                                            name="email"
                                                            value={formData.email}
                                                            onChange={handleInputChange}
                                                            className="form-control"
                                                            required
                                                            disabled={loading}
                                                        />
                                                    ) : (
                                                        <p className="mb-0 fw-medium">{profileData.email}</p>
                                                    )}
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label text-muted small">Phone</label>
                                                    {isEditing ? (
                                                        <input
                                                            type="tel"
                                                            name="phoneNumber"
                                                            value={formData.phoneNumber}
                                                            onChange={handleInputChange}
                                                            className="form-control"
                                                            disabled={loading}
                                                        />
                                                    ) : (
                                                        <p className="mb-0 fw-medium">{profileData.phoneNumber || 'Not provided'}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column - Business Information */}
                                    <div className="col-lg-8">
                                        <div className="card border-0 shadow-sm mb-4">
                                            <div className="card-header bg-white">
                                                <h5 className="mb-0 fw-bold">Personal Information</h5>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label text-muted small">Username</label>
                                                        <p className="mb-0 fw-medium">{profileData.userName}</p>
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label text-muted small">City</label>
                                                        {isEditing ? (
                                                            <input
                                                                type="text"
                                                                name="city"
                                                                value={formData.city}
                                                                onChange={handleInputChange}
                                                                className="form-control"
                                                                disabled={loading}
                                                            />
                                                        ) : (
                                                            <p className="mb-0 fw-medium">{profileData.city || 'Not provided'}</p>
                                                        )}
                                                    </div>
                                                    <div className="col-12 mb-3">
                                                        <label className="form-label text-muted small">Address</label>
                                                        {isEditing ? (
                                                            <textarea
                                                                name="address"
                                                                value={formData.address}
                                                                onChange={handleInputChange}
                                                                className="form-control"
                                                                rows="2"
                                                                disabled={loading}
                                                            />
                                                        ) : (
                                                            <p className="mb-0 fw-medium">{profileData.address || 'Not provided'}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Account Information */}
                                        <div className="card border-0 shadow-sm">
                                            <div className="card-header bg-white">
                                                <h5 className="mb-0 fw-bold">Account Information</h5>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label text-muted small">Status</label>
                                                        <p className="mb-0 fw-medium">
                                                            <span className={`badge ${profileData.isActive ? 'bg-success' : 'bg-danger'}`}>
                                                                {profileData.isActive ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </p>
                                                    </div>
                                                    <div className="col-md-6 mb-3">
                                                        <label className="form-label text-muted small">Last Login</label>
                                                        <p className="mb-0 fw-medium">Today</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;